import React, { useState, useEffect } from 'react';
import { Shell } from '../components/Shell';
import { Sidebar } from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Loader from '../components/Loader';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Non-React module scope: labels resolve from the stored language directly
// (same pattern as utils/auth.js). Option VALUES stay unchanged.
const L = (sq, en) => (localStorage.getItem('k360-lang') === 'en' ? en : sq);

const FUGITIVE_SOURCES = [
  { value: 'refrigeration', label: L('Sistemi i ftohjes (frigoriferë, AC)', 'Refrigeration system (fridges, AC)') },
  { value: 'fire_suppression', label: L('Sistemi i shuarjes së zjarrit', 'Fire suppression system') },
  { value: 'air_conditioning', label: L('Kondicionimi i ajrit (HVAC)', 'Air conditioning (HVAC)') },
  { value: 'foam_blowing', label: L('Agjentë shkumëzues', 'Foam blowing agents') },
  { value: 'industrial_gases', label: L('Gazra industriale', 'Industrial gases') },
  { value: 'other', label: L('Tjetër', 'Other') }
];

const REFRIGERANT_TYPES = [
  { value: 'hfc_134a', label: 'HFC-134a', gwp: 1430 },
  { value: 'hfc_410a', label: 'HFC-410A (R-410A)', gwp: 2088 },
  { value: 'hfc_404a', label: 'HFC-404A', gwp: 3922 },
  { value: 'hfc_407c', label: 'HFC-407C', gwp: 1774 },
  { value: 'hfc_32', label: 'HFC-32 (R-32)', gwp: 675 },
  { value: 'r22', label: 'R-22 (HCFC-22)', gwp: 1810 },
  { value: 'r410a', label: 'R-410A', gwp: 2088 },
  { value: 'sf6', label: 'SF₆', gwp: 22800 },
  { value: 'co2_refrigerant', label: 'CO₂ (R-744)', gwp: 1 },
  { value: 'other', label: L('Tjetër', 'Other') }
];

export const Scope1FugitivePage = () => {
  const { t } = useT();
  const [formData, setFormData] = useState({
    buildingId: '',
    source: '',
    refrigerantType: '',
    period: getCurrentMonth(),
    quantity: '',
    unit: 'kg'
  });
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    fetch(`${API_BASE}/api/buildings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const list = Array.isArray(data) ? data : data.buildings || [];
        setBuildings(list.filter(b => !b.archived));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setCalcResult(null);
    setIsSaved(false);
    setError('');
  };

  const handleCalculate = () => {
    const { source, refrigerantType, quantity } = formData;
    if (!source || !refrigerantType || !quantity || parseFloat(quantity) <= 0) {
      setError(t('s1fug.fillAllFields'));
      return;
    }

    setCalculating(true);
    setError('');

    const ref = REFRIGERANT_TYPES.find(r => r.value === refrigerantType);
    const gwp = ref?.gwp || 1;
    const qty = parseFloat(quantity);
    
    // Fugitive emissions = quantity (kg) × GWP / 1000 → tCO2e
    const emissionsTco2e = (qty * gwp) / 1000;

    setCalcResult({
      emissions_tco2e: emissionsTco2e,
      gwp,
      refrigerantLabel: ref?.label || refrigerantType,
      formula: `${qty} kg × ${gwp} GWP / 1000 = ${emissionsTco2e.toFixed(6)} tCO₂e`
    });
    setCalculating(false);
  };

  const handleSave = async () => {
    if (!calcResult || isSaved) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/';
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        buildingId: parseInt(formData.buildingId),
        processType: 'fugitive',
        fuelKey: formData.refrigerantType,
        period: formData.period,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        energy_kWh: 0,
        emissions_tCO2e: calcResult.emissions_tco2e,
        source: formData.source
      };

      const response = await fetch(`${API_BASE}/api/emissions/scope1/process/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }

      if (response.status === 409) {
        setError(t('s1fug.duplicate'));
        setSaving(false);
        return;
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Save failed');
      }

      setIsSaved(true);
    } catch (err) {
      setError(t('s1fug.saveError', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      buildingId: formData.buildingId,
      source: '',
      refrigerantType: '',
      period: getCurrentMonth(),
      quantity: '',
      unit: 'kg'
    });
    setCalcResult(null);
    setIsSaved(false);
    setError('');
  };

  if (loading) {
    return (
      <Shell sidebar={<Sidebar />}>
        <Navbar pageTitle={t('s1fug.title')} />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Loader />
        </div>
      </Shell>
    );
  }

  return (
    <Shell sidebar={<Sidebar />}>
      <Navbar pageTitle={t('s1fug.title')} />
      <div style={{ padding: '24px', maxWidth: '800px' }}>
        
        <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          
          {/* Building Select */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
              {t('s1fug.building')}
            </label>
            <select
              value={formData.buildingId}
              onChange={(e) => handleChange('buildingId', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: '#fff' }}
            >
              <option value="">{t('s1fug.selectBuilding')}</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Period */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
              {t('s1fug.period')}
            </label>
            <input
              type="month"
              value={formData.period}
              onChange={(e) => handleChange('period', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
            />
          </div>

          {/* Source */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
              {t('s1fug.source')}
            </label>
            <select
              value={formData.source}
              onChange={(e) => handleChange('source', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: '#fff' }}
            >
              <option value="">{t('s1fug.selectSource')}</option>
              {FUGITIVE_SOURCES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Refrigerant Type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
              {t('s1fug.gasType')}
            </label>
            <select
              value={formData.refrigerantType}
              onChange={(e) => handleChange('refrigerantType', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: '#fff' }}
            >
              <option value="">{t('s1fug.selectType')}</option>
              {REFRIGERANT_TYPES.map(r => (
                <option key={r.value} value={r.value}>{r.label} (GWP: {r.gwp})</option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
              {t('s1fug.quantity')}
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              placeholder={t('s1fug.qtyPlaceholder')}
              min="0"
              step="0.01"
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <button
              onClick={handleCalculate}
              disabled={calculating || !formData.buildingId || !formData.source || !formData.refrigerantType || !formData.quantity}
              style={{
                flex: 1, padding: '12px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 600,
                background: '#3fffd8', color: '#1a1a1a', cursor: 'pointer',
                opacity: (calculating || !formData.buildingId || !formData.source || !formData.refrigerantType || !formData.quantity) ? 0.5 : 1
              }}
            >
              {calculating ? t('s1fug.calculating') : t('s1fug.calculate')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || isSaved || !calcResult}
              style={{
                flex: 1, padding: '12px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 600,
                background: isSaved ? '#3fffd8' : '#1f2937', color: '#fff', cursor: 'pointer',
                opacity: (saving || isSaved || !calcResult) ? 0.6 : 1
              }}
            >
              {saving ? t('s1fug.saving') : isSaved ? t('s1fug.savedBadge') : t('s1fug.save')}
            </button>
            <button
              onClick={handleReset}
              style={{
                padding: '12px 20px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', fontWeight: 500,
                background: '#fff', color: '#374151', cursor: 'pointer'
              }}
            >
              {t('s1fug.clear')}
            </button>
          </div>

          {/* Result */}
          {calcResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '20px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: '#166534' }}>
                {t('s1fug.result')}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('s1fug.gasRefrigerant')}</span>
                  <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 600 }}>{calcResult.refrigerantLabel}</p>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>GWP</span>
                  <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 600 }}>{calcResult.gwp}</p>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('s1fug.emissions')}</span>
                  <p style={{ margin: '2px 0 0', fontSize: '22px', fontWeight: 700, color: '#166534' }}>
                    {calcResult.emissions_tco2e.toFixed(6)} tCO₂e
                  </p>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Formula</span>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {calcResult.formula}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
};

export default Scope1FugitivePage;
